import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/layout/PublicHeader";
import { useSEO } from "@/lib/useSEO";
import {
  ArrowRight,
  CheckCircle2,
  Crown,
  Sparkles,
  Clock,
  Building2,
  GraduationCap,
  ShieldCheck,
} from "lucide-react";

const PLAN_FEATURES = {
  free: ["5 CV sends", "Smart match ranking", "Platform inbox", "Basic tracking"],
  basic: [
    "100 applications / month",
    "Tailored cover letters",
    "CV review unlimited",
    "AI Mock Interview (2/mo) — Marcus voice",
    "Advanced tracking",
  ],
  pro: [
    "Unlimited applications",
    "AI Mock Interview — unlimited",
    "Deal Closer + Career GPS",
    "Interview coach + CV review",
    "Salary intelligence",
    "Private recruiter intros",
    "Certificates & Courses — included",
  ],
  agency: [
    "Every feature — unlocked",
    "AI Mock Interview — unlimited",
    "Multi-seat agency dashboard",
    "Full Recruiter Portal access",
    "Bulk candidate outreach",
    "API / whitelabel option",
    "Certificates & Courses — included",
  ],
};

const ICON = { free: Clock, basic: Sparkles, pro: Crown, agency: Building2 };

export default function Subscriptions() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  useSEO({
    title: "Pricing & Plans — Maqam",
    description: "Maqam pricing — Free, Basic, Pro, and Agency plans. Saudi Arabia's AI job-application platform. Medical OSCE Pro from €150/mo.",
    path: "/subscriptions",
  });

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);

  const renderPeriod = (p) =>
    p.period === "trial" ? "· 7-day trial" : p.period === "year" ? "/ year" : "/ month";

  const ctaHref = user ? "/app/billing" : "/register";

  return (
    <div className="min-h-screen bg-white text-black" data-testid="subscriptions-page">
      <PublicHeader />

      {/* Hero */}
      <section className="border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">
            / Subscriptions
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl md:text-7xl tracking-tighter leading-[0.95]">
            Pick the plan that fits
            <br />
            <span className="text-gradient">your hustle.</span>
          </h1>
          <p className="text-gray-600 text-lg mt-6 max-w-2xl mx-auto">
            Start free for 7 days. Upgrade when you're ready. Agencies get a full-feature yearly license.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6" data-testid="sub-plans">
            {plans.map((p) => {
              const Icon = ICON[p.id] || Sparkles;
              const features = PLAN_FEATURES[p.id] || [];
              const isHot = p.id === "basic";
              const isAgency = p.id === "agency";
              return (
                <div
                  key={p.id}
                  className={`p-8 border relative flex flex-col transition-all hover:translate-y-[-4px] ${
                    isAgency
                      ? "bg-[#0033FF] text-white border-[#0033FF]"
                      : isHot
                      ? "bg-black text-white border-black"
                      : "bg-white border-black/10"
                  }`}
                  data-testid={`sub-plan-${p.id}`}
                >
                  {isHot && (
                    <div className="absolute -top-3 start-6 bg-[#FF3B30] text-white text-xs font-bold px-3 py-1">
                      MOST POPULAR
                    </div>
                  )}
                  {isAgency && (
                    <div className="absolute -top-3 start-6 bg-[#34C759] text-white text-xs font-bold px-3 py-1">
                      FOR AGENCIES
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-4">
                    <Icon className={`w-4 h-4 ${isAgency ? "text-white" : isHot ? "text-[#FF3B30]" : "text-[#0033FF]"}`} />
                    <span
                      className={`font-mono text-xs uppercase tracking-widest ${
                        isAgency ? "text-white/80" : isHot ? "text-[#FF3B30]" : "text-gray-500"
                      }`}
                    >
                      {p.name}
                    </span>
                  </div>
                  <div className="mb-6">
                    <div className="font-display font-black text-4xl tracking-tighter">{p.display}</div>
                    <div
                      className={`font-mono text-xs mt-1 ${
                        isAgency || isHot ? "text-white/70" : "text-gray-500"
                      }`}
                    >
                      {renderPeriod(p)}
                    </div>
                    {isAgency && (
                      <div className="mt-3 text-xs bg-white/10 border border-white/20 p-3">
                        <div className="flex justify-between">
                          <span>License</span>
                          <span className="font-bold">SAR 30,000 / yr</span>
                        </div>
                        <div className="flex justify-between">
                          <span>IT support</span>
                          <span className="font-bold">SAR 3,000 / yr</span>
                        </div>
                        <div className="border-t border-white/20 mt-2 pt-2 flex justify-between font-bold">
                          <span>Total</span>
                          <span>SAR 33,000 / yr</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <ul className="space-y-2 mb-8 text-sm flex-1">
                    {features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <CheckCircle2
                          className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                            isAgency ? "text-white" : isHot ? "text-[#34C759]" : "text-[#0033FF]"
                          }`}
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={ctaHref}
                    className={`block text-center font-bold py-3 transition-colors ${
                      isAgency
                        ? "bg-white text-[#0033FF] hover:bg-black hover:text-white"
                        : isHot
                        ? "bg-white text-black hover:bg-[#FF3B30] hover:text-white"
                        : "bg-black text-white hover:bg-[#0033FF]"
                    }`}
                    data-testid={`sub-cta-${p.id}`}
                  >
                    {p.id === "free" ? "Start free" : `Choose ${p.name}`}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Certificates Add-on callout */}
          <div
            className="mt-10 border border-black/10 bg-[#F8F9FA] p-6 md:p-8 flex flex-wrap items-center gap-6"
            data-testid="certs-addon-callout"
          >
            <div className="w-14 h-14 bg-black text-white flex items-center justify-center">
              <GraduationCap className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#0033FF] mb-1">
                Certificates & Courses — premium add-on
              </div>
              <div className="font-display font-bold text-2xl tracking-tight">
                SAR 50 / month · or free with Pro &amp; Agency
              </div>
              <p className="text-sm text-gray-600 mt-2 max-w-2xl">
                Unlock our live course search across Coursera, edX, Udemy, LinkedIn Learning,
                AWS, Google Cloud, Microsoft Learn, IBM SkillsBuild, Doroob and Misk Academy.
                Manual monthly renewal — no auto-bill.
              </p>
            </div>
            <Link
              to={user ? "/app/certificates" : "/register"}
              className="bg-black text-white hover:bg-[#0033FF] font-bold px-6 py-3 flex items-center gap-2 transition-colors"
              data-testid="certs-addon-cta"
            >
              Explore courses <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div
            className="mt-6 border border-black/10 bg-white p-5 flex items-start gap-3 text-xs text-gray-600"
            data-testid="sub-agency-disclaimer"
          >
            <ShieldCheck className="w-4 h-4 text-[#0033FF] mt-0.5 shrink-0" />
            <span>
              <span className="font-bold text-black">Agency use requires the Agency License.</span>{" "}
              Individual tiers (Trial, Basic, Pro) are for a single jobseeker — running
              applications on behalf of clients under Basic or Pro is not permitted.
            </span>
          </div>
        </div>
      </section>

      <footer className="border-t border-black/10 bg-black text-gray-500 text-sm">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 flex flex-wrap gap-4 justify-between">
          <div>© 2026 Maqam. Built for the Kingdom.</div>
          <div className="flex gap-6">
            <span>Privacy</span>
            <span>Terms</span>
            <span>Contact</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
